/**
 * Contrôle des formulaires — module PUR.
 *
 * ── Ce qu'il répare ─────────────────────────────────────────────────────────
 *
 * Les formulaires signalaient un champ obligatoire manquant en le passant au
 * rouge, et rien de plus. Trois manques constatés par la RQ le 14/08/2026 sur
 * la fiche d'équipement, vrais partout ailleurs :
 *
 *  · le rouge ne se voit pas quand le champ est HORS ÉCRAN. Sur une fiche
 *    longue, on clique « Enregistrer » depuis le bas, rien ne se passe en
 *    apparence, et le champ fautif est huit cents pixels plus haut ;
 *  · rien ne DIT ce qui manque : il faut remonter et chercher le rouge à l'œil ;
 *  · un champ simplement VIDE — non obligatoire — ne se distingue pas d'un
 *    champ rempli tant qu'on ne le lit pas. Or « pas encore renseigné » est
 *    exactement ce qu'un RQ veut repérer d'un coup d'œil sur une fiche en cours.
 *
 * ── Les deux natures de problème, jamais confondues ─────────────────────────
 *
 * `manquant` — obligatoire et vide. `errone` — rempli mais invalide (date
 * impossible, format refusé, valeur hors bornes). Ce ne sont pas deux nuances
 * du même défaut : le premier demande de SAISIR, le second de CORRIGER, et le
 * message doit dire lequel. Les fondre en « champs invalides » oblige le
 * lecteur à rouvrir chaque champ pour comprendre ce qu'on attend de lui.
 *
 * ── L'ordre est celui du formulaire, et c'est ce qui rend le saut utile ─────
 *
 * Les champs sont déclarés dans l'ordre où ils s'affichent. Le PREMIER problème
 * est donc le plus haut à l'écran : c'est vers lui qu'on saute, et repartir de
 * là fait descendre naturellement vers les suivants. Trier par gravité ou par
 * ordre alphabétique ferait sauter au hasard dans la page.
 */

/** Un champ tel que le formulaire le déclare, dans l'ordre d'affichage. */
export interface ChampDeclare {
  /** `id` de l'élément dans le DOM — c'est lui qui permet d'y sauter. */
  id: string;
  /** Nom lisible, celui de l'étiquette. Il part tel quel dans le message. */
  label: string;
  valeur: unknown;
  requis?: boolean;
  /** Message si la valeur est PRÉSENTE mais invalide. `null` = rien à dire. */
  erreur?: string | null;
}

export interface Probleme {
  id: string;
  label: string;
  nature: 'manquant' | 'errone';
  message: string;
}

/**
 * Un champ est-il vide ?
 *
 * ⚠️ `0` et `false` ne sont PAS vides, et c'est le piège classique de ce genre
 * de fonction : un nombre de résultats concernés à 0, une case décochée, une
 * durée nulle sont des RÉPONSES. Les traiter comme des vides ferait réclamer
 * une saisie déjà faite — et, pire, empêcherait d'enregistrer une valeur
 * légitime. On ne teste donc jamais la véracité de la valeur.
 */
export function estVide(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Les problèmes du formulaire, dans l'ordre d'affichage des champs. */
export function problemes(champs: ChampDeclare[]): Probleme[] {
  const out: Probleme[] = [];
  for (const c of champs) {
    const vide = estVide(c.valeur);
    if (c.requis && vide) {
      out.push({ id: c.id, label: c.label, nature: 'manquant', message: `${c.label} — à renseigner` });
      // Un champ vide ne peut pas être en plus « erroné » : la seule chose à
      // en dire est qu'il manque. Empiler les deux doublerait la ligne.
      continue;
    }
    const err = String(c.erreur || '').trim();
    if (err) out.push({ id: c.id, label: c.label, nature: 'errone', message: `${c.label} — ${err}` });
  }
  return out;
}

/** Les champs VIDES non obligatoires — ceux qu'on veut repérer à l'œil. */
export function champsVides(champs: ChampDeclare[]): string[] {
  return champs.filter((c) => !c.requis && estVide(c.valeur)).map((c) => c.id);
}

/**
 * Phrase du bandeau. Elle compte, et distingue les deux natures.
 *
 * « 3 problèmes » ne dit rien d'actionnable ; « 2 champs à renseigner et
 * 1 valeur à corriger » dit quoi faire, et l'ordre des deux membres suit celui
 * du travail : on saisit d'abord, on corrige ensuite.
 */
export function resume(pbs: Probleme[]): string {
  const m = pbs.filter((p) => p.nature === 'manquant').length;
  const e = pbs.filter((p) => p.nature === 'errone').length;
  const bouts: string[] = [];
  if (m) bouts.push(`${m} champ${m > 1 ? 's' : ''} obligatoire${m > 1 ? 's' : ''} à renseigner`);
  if (e) bouts.push(`${e} valeur${e > 1 ? 's' : ''} à corriger`);
  if (!bouts.length) return '';
  return `Enregistrement impossible : ${bouts.join(' et ')}.`;
}
